import {
  Button,
  Menu,
  MenuTrigger,
  MenuTriggerChildProps,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components';
import { MoreHorizontalRegular, Open24Regular } from '@fluentui/react-icons';
import cn from 'classnames';
import moment from 'moment-timezone';
import React, { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyAvatarGroup } from '../../Avatar';
import { AssignmentItem } from './AssignmentsWidget.interfaces';

export interface AssignmentsWidgetListProps {
  assignments: AssignmentItem[];
  onAssignmentSelected?: (assignment: AssignmentItem) => void;
  onOpenInBrowser?: (assignment: AssignmentItem) => void;
  className?: string;
}

export const AssignmentsWidgetList: React.FC<AssignmentsWidgetListProps> = ({
  assignments,
  onAssignmentSelected,
  onOpenInBrowser,
  className,
}) => {
  const { t } = useTranslation();

  return (
    <div className={cn('list', 'assignmentsWidgetList', className)}>
      {assignments.map((assignment) => {
        const isClickable = Boolean(onAssignmentSelected);
        const interactiveProps = isClickable
          ? {
              tabIndex: 0,
              role: 'link',
              onClick: () => onAssignmentSelected?.(assignment),
              onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
                if (event.key === 'Enter') {
                  onAssignmentSelected?.(assignment);
                }
              },
            }
          : {};

        return (
          <div
            aria-label={t('dashboard:assignments:navigation_assignment', {
              defaultValue:
                'Navigation zur Unterseite der Aufgabe: {{subject}} in Kurs {{courseNames}} fällig am {{dueDate}}',
              subject: assignment.subject,
              courseNames: assignment.courses.map((course) => course.name).join(', '),
              dueDate: moment(assignment.dueDate).format('DD.MM.YYYY'),
            })}
            key={assignment.id}
            className={cn('listEntry', { 'listEntry--inert': !isClickable })}
            {...interactiveProps}
          >
            <div className="dueDate">{moment(assignment.dueDate).format('DD.MM.')}</div>
            <div className="groupAvatars">
              <LazyAvatarGroup
                shape="square"
                groupId={assignment.azureGroupId || assignment.courses[0]?.id || assignment.id}
                name={assignment.courses[0]?.name || assignment.subject}
              />
            </div>
            <div className="assignmentInfo">
              <span>{assignment.subject}</span>
              <div className="courseName">
                {assignment.courses.map((course) => course.name).join(', ')}
              </div>
            </div>
            {onOpenInBrowser && (
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  {(props: MenuTriggerChildProps<'button'>) => (
                    <Button
                      {...props}
                      appearance="transparent"
                      icon={<MoreHorizontalRegular />}
                      aria-label={t('dashboard:assignments:menu_aria_label', {
                        defaultValue: 'Weitere Aktionen',
                      })}
                    />
                  )}
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem icon={<Open24Regular />} onClick={() => onOpenInBrowser(assignment)}>
                      {t('dashboard:assignments:open_in_browser', {
                        defaultValue: 'In MNSpro im Browser öffnen',
                      })}
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            )}
          </div>
        );
      })}
    </div>
  );
};
